import { message, Modal, notification } from "antd";
import { Adjectives } from "./Adjective";
import { FrontPageImages } from "./FrontPageImages";
import mapApi from "services/MapApi";
import { errorLog } from "logging";
import { Cookies } from "react-cookie";

const COOKIE_USER_INFO = "hay2u_uif";
const COOKIE_USER_ADDRESS = "hay2u_uad";
const COOKIE_ACCESS_TOKEN = "hay2u_atk";
const COOKIE_REFRESH_TOKEN = "hay2u_rtk";
const COOKIE_GALLERY = "hay2u_glr";

const cookies = new Cookies();
const setCookie = (name: string, value: string): void =>
  cookies.set(name, value, { path: "/" });
const getCookie = (name: string): string => cookies.get(name) || "";
const removeCookie = (name: string): void => cookies.remove(name, { path: "/" });

class Helpers {
  getRefreshToken = (): string => getCookie(COOKIE_REFRESH_TOKEN);

  storeRefreshToken = (refreshToken: string): void =>
    setCookie(COOKIE_REFRESH_TOKEN, refreshToken);

  getAccessToken = (): string => getCookie(COOKIE_ACCESS_TOKEN);

  storeAccessToken = (accessToken: string): void =>
    setCookie(COOKIE_ACCESS_TOKEN, accessToken);

  clearToken = (): void => {
    removeCookie(COOKIE_REFRESH_TOKEN);
    removeCookie(COOKIE_ACCESS_TOKEN);
  };

  getUserInfo = () => {
    const userInfo: any = getCookie(COOKIE_USER_INFO);
    return userInfo !== "" ? userInfo : null;
  };

  getUserAddress = () => {
    const userAddress: any = getCookie(COOKIE_USER_ADDRESS);
    return userAddress != "" ? userAddress : null;
  };

  storeUserAddress = (userAddress: object) => {
    const userAddressString: any = JSON.stringify(userAddress);
    setCookie(COOKIE_USER_ADDRESS, userAddressString);
  };

  removeUserAddress = (): void => {
    removeCookie(COOKIE_USER_ADDRESS);
  };

  storeUserInfo = (userInfo: object): void => {
    const userInfoString = JSON.stringify(userInfo);
    setCookie(COOKIE_USER_INFO, userInfoString);
  };

  removeUserInfo = (): void => {
    removeCookie(COOKIE_USER_INFO);
  };

  storeGallery = (gallery: any) => {
    const galleryString = JSON.stringify(gallery);
    setCookie(COOKIE_GALLERY, galleryString);
  };

  removeGallery = (): void => {
    removeCookie(COOKIE_GALLERY);
  };

  getGallery = () => {
    const galleryString: any = getCookie(COOKIE_GALLERY);
    return galleryString !== "" ? galleryString : null;
  };

  isLoggedIn = (): boolean => {
    return this.getUserInfo() == null || this.getUserInfo() === undefined
      ? false
      : true;
  };

  decodeToken = (token: string) => {
    var base64Url = token.split(".")[1];
    var base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    var jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );

    return JSON.parse(jsonPayload);
  };

  notification = {
    success: (content: string) => {
      message.open({
        type: "success",
        content: content,
      });
    },
    error: (content: string) => {
      message.open({
        type: "error",
        content: content,
      });
    },
    warning: (content: string) => {
      message.open({
        type: "warning",
        content: content,
      });
    },
  };

  redirect = (url: string) => {
    window.location.href = url;
  };

  showUpdatingNotification = (
    key: string,
    message = "Updating Data",
    descriptionText = "Data is being updated, please wait..."
  ) => {
    notification.open({
      key,
      message,
      description: (
        <div>
          <p>{descriptionText} </p>
          <div className="loader"></div>
        </div>
      ),
      duration: 0,
      placement: "bottomLeft",
    });
  };

  closeNotification = (key: string) => {
    notification.destroy(key);
  };

  showSuccessUpdated = (description: string) => {
    notification.success({
      message: "Success",
      description,
      placement: "bottomLeft",
      duration: 2,
    });
  };

  showErrorUpdated = (message: string, description: string) => {
    notification.error({
      message,
      description,
      placement: "bottomLeft",
      duration: 2,
    });
  };

  generateUsername = (firstName: string, city: string) => {
    const userNameList: string[] = [];
    Array.from({ length: 5 }).map((_, index) => {
      const randomAdjective =
        Adjectives[Math.floor(Math.random() * Adjectives.length)];
      const userNameItem = `${randomAdjective}-${firstName}-${city}`;
      userNameList.push(userNameItem);
    });

    return userNameList;
  };

  getImageFrontPage = (key: string) => {
    const image = FrontPageImages.find((item) => item.name === key)?.url;
    return image;
  };

  fetchZipCode = async (lat: any, lon: any) => {
    try {
      const response = await mapApi.detectZipCode(lat, lon);
      if (response.status === 200) {
        return response.data.features[0].properties.postcode;
      }
    } catch (error) {
      console.error("Error fetching address:", error);
      errorLog("Helpers.tsx - fetchZipCode", error);
    }
  };

  handleSearchLocation = async (query: string) => {
    try {
      const response = await mapApi.searchLocation(query);
      if (response.status === 200 && Array.isArray(response.data)) {
        return response.data;
      }
      return [];
    } catch (error) {
      // An unreachable or failing geocoder is an empty result set, not an
      // undefined value: callers render this list, so returning undefined
      // would crash the suggestion box instead of letting the user type the
      // address by hand.
      errorLog("Helpers.tsx - handleSearchLocation", error);
      return [];
    }
  };

  convertSecondsToHoursAndMinutes = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours} hours ${minutes} minutes`;
  };

  getCroppedImg = async (imageSrc: any, crop: any) => {
    const image = await this.createImage(imageSrc);
    const canvas = document.createElement("canvas");
    const ctx: any = canvas.getContext("2d");

    canvas.width = crop.width;
    canvas.height = crop.height;

    ctx.drawImage(
      image,
      crop.x,
      crop.y,
      crop.width,
      crop.height,
      0,
      0,
      crop.width,
      crop.height
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob: any) => {
        resolve(URL.createObjectURL(blob));
      }, "image/jpeg");
    });
  };

  createImage = (url: string) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = (error) => reject(error);
      img.src = url;
    });
  };

  formatNumber = (num: any, digit: number) => {
    num = Number(num);
    if (isNaN(num)) return "";

    const factor = Math.pow(10, digit);
    const truncated = Math.trunc(num * factor) / factor;

    return truncated.toLocaleString("en-US", {
      minimumFractionDigits: digit,
      maximumFractionDigits: digit,
    });
  };

  formatNumberInput = (num: any) => {
    if (num === 0) return num;
    if (!num) return "";

    const [integerPart, decimalPart] = num.toString().split(".");
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    return decimalPart !== undefined
      ? `${formattedInteger}.${decimalPart}`
      : formattedInteger;
  };

  parseNumber = (num: any) => {
    return num.replace(/,/g, "");
  };

  isISODateString = (str: string) => {
    const isoRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z?$/;
    return isoRegex.test(str);
  };

  formatDateTime = (isoString: string, message?: boolean) => {
    if (!this.isISODateString(isoString)) return isoString;
    isoString =
      isoString[isoString.length - 1] !== "Z" ? isoString + "Z" : isoString;
    const date = new Date(isoString);
    if (message) {
      return new Intl.DateTimeFormat(undefined, {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }).format(date);
    }
    return new Intl.DateTimeFormat("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }).format(date);
  };

  getCurrentDateTime = () => {
    const now = new Date();
    return now
      .toLocaleString("en-US", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      })
      .replace(/\//g, "-");
  };

  HaversineDistance = (
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ) => {
    const R = 6371;
    const toRad = (angle: any) => (angle * Math.PI) / 180;

    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  formatTimeAgo = (timestamp: string) => {
    const now: Date = new Date();
    const past: Date = new Date(timestamp);

    const timezoneOffset = now.getTimezoneOffset() * 60000;
    const localPast = new Date(past.getTime() - timezoneOffset);

    const diffInSeconds = Math.floor(
      (now.getTime() - localPast.getTime()) / 1000
    );

    if (diffInSeconds < 60)
      return `${diffInSeconds} second${diffInSeconds !== 1 ? "s" : ""} ago`;

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60)
      return `${diffInMinutes} minute${diffInMinutes !== 1 ? "s" : ""} ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24)
      return `${diffInHours} hour${diffInHours !== 1 ? "s" : ""} ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} day${diffInDays !== 1 ? "s" : ""} ago`;
  };

  hideCardNumber = (card_num: string) => {
    return (
      "**** **** **** " + card_num.slice(card_num.length - 4, card_num.length)
    );
  };

  moveValueToFirst = (arr: any, value: any) => {
    const newArr = arr.filter((item: any) =>
      item.every((val: any, index: number) => val !== value[index])
    );
    return [value, ...newArr];
  };

  getFileExtension = (url: string): string => {
    return url.split(".").pop()?.toLowerCase() || "";
  };

  isImage = (ext: string): boolean => {
    return ["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(ext);
  };

  getExtensionFromDataUrl = (dataUrl: string): string => {
    const match = dataUrl.match(/^data:(.*?);base64,/);
    if (!match) return "";

    const mimeType = match[1];

    // Map mime types to extensions
    const mimeToExt: Record<string, string> = {
      "image/jpeg": "jpg",
      "image/png": "png",
      "image/gif": "gif",
      "image/webp": "webp",
      "video/mp4": "mp4",
      "application/pdf": "pdf",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
        "xlsx",
      "application/msword": "doc",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        "docx",
      "application/vnd.ms-excel": "xls",
    };

    return mimeToExt[mimeType] || "";
  };

  isCDNUrl = (str: string): boolean => {
    return /^https?:\/\//.test(str);
  };

  extractFileName = (input: string): string => {
    if (input.startsWith("data:") && input.includes(";base64,")) {
      const mimeMatch = input.match(/^data:(.*?);base64,/);
      const mimeType = mimeMatch ? mimeMatch[1] : "application/octet-stream";

      const mimeToExt: Record<string, string> = {
        "image/jpeg": "jpg",
        "image/png": "png",
        "image/gif": "gif",
        "image/webp": "webp",
        "application/pdf": "pdf",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
          "xlsx",
        "application/msword": "doc",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
          "docx",
        "application/vnd.ms-excel": "xls",
      };

      const ext = mimeToExt[mimeType] || "bin";
      return `${Date.now() + 1}.${ext}`;
    }

    if (/^https?:\/\//.test(input)) {
      const segments = input.split("?");
      const path = segments[0];
      return path.substring(path.lastIndexOf("/") + 1);
    }

    return `unknown_file_${Date.now()}`;
  };

  toFixedNoRound = (num: number, digits: number) => {
    const factor = Math.pow(10, digits);
    return Math.floor(num * factor) / factor;
  };

  confirmModal = (content: string, onOkCallback: () => void) => {
    Modal.confirm({
      title: "Confirm",
      content: (
        <div>
          <p>{content}</p>
        </div>
      ),
      onOk: () => onOkCallback(),
    });
  };
}

const instance = new Helpers();
export default instance;
